import React, { useEffect, useState } from "react";

// ui components
import "react-datepicker/dist/react-datepicker.css";
import styled from "styled-components";

// utilities
import moment from 'moment';
import * as XLSX from 'xlsx';
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// redux functions
import { getJenisDokumenAsync } from "../../reducers/jenisDokumen/jenisDokumen.actions";
import { getDokumenKerjasamaAsync } from "../../reducers/dokumenKerjasama/dokumenKerjasama.actions";

// data types
import { RootState } from "../../store/rootReducer";
import { PartnerRequest } from "../../interfaces/Partner";
import { BentukKerjasama } from "../../interfaces/BentukKerjasama";
import { objectToParams, paramsToObject } from "../../store/api";

export const DokumenKerjasamaExportExcecl = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const isLoading = useSelector((state: RootState) => state.dokumenKerjasama.isLoading);
  const errorMessage = useSelector((state: RootState) => state.dokumenKerjasama.errorMessage);
  const listDokumenKerjasama = useSelector((state: RootState) => state.dokumenKerjasama.listDokumenKerjasama);


  useEffect(() => {
    refresh();
  }, []);

  /**
   * Execute API calls based on filter provided in url
   * get parameters.
   * - Will only send filter parameters that exist.
   * - If no params were found, will set today's date
   *   as default
   */
  const refresh = () => {
    if (isLoading) return;
    const filter = paramsToObject(location.search);
    if (!filter) {
      history.push(`/dokumen-kerjasama/export${objectToParams({
        tanggal_awal: moment(new Date()).format('YYYY-MM-DD'),
        tanggal_akhir: moment(new Date()).format('YYYY-MM-DD')
      })}`);
      return;
    };
    if (filter.jenis_dokumen && filter.jenis_dokumen < 0) delete filter.jenis_dokumen;
    if (!filter.judul_kerjasama) delete filter.judul_kerjasama;
    if (!filter.tanggal_awal) delete filter.tanggal_awal;
    if (!filter.tanggal_akhir) delete filter.tanggal_akhir;
    if (!filter.tanggal_awal || !filter.tanggal_akhir) return;
    if (!filter.sisa_berlaku) delete filter.sisa_berlaku;
    dispatch(getJenisDokumenAsync.request({}));
    dispatch(getDokumenKerjasamaAsync.request(filter));
  }

  /**
   * Export table results into excel
   **/
  const exportExcel = () => {
    const table = document.getElementById("main-table");
    const workspace = XLSX.utils.table_to_book(table);
    XLSX.writeFile(workspace, "dokumen-kerjasama.xlsx");
  }

  /** 
   * Render function for bentuk kerjasama field
   * in table
   **/
  const renderBentukKerjasama = (bentukKerjasama: BentukKerjasama[]) => {
    if (bentukKerjasama.length > 0) {
      return bentukKerjasama.map(item => item.NAMA).join(', ');
    } else {
      return '—';
    }
  }

  /**
   * Render function for list of companies for
   * bentuk kerjasama
   **/
  const renderCompanies = (companies: PartnerRequest[]) => {
    const newCompanies = [...companies];
    newCompanies.splice(0, 1);
    return newCompanies.map((company, index) => {
      return (
        <tr key={index}>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td>{company.CO_NEGARA_DETAIL.name}</td>
          <td>{company.CO_NAME}</td>
          <td>{company.CO_TYP_DETAIL.CO_TYP_NAME}</td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      );
    });
  }

  /**
   * Renders list of documents from API calls.
   * - Should error recieved, will display error
   *   message along with refresh button.
   * - Should status is still loading, a loading
   *   message with no action will be displayed.
   * - Should data does not exist, notify user to
   *   refresh or change filter.
   * - Should data exist, returns table row.
   */
  const renderDokumenKerjasama = () => {
    if (errorMessage) {
      return (
        <tr>
          <td colSpan={9} style={{textAlign: 'center'}}>
          Terdapat kesalahan dalam memuat data: <b>{errorMessage}</b><br/>
          <button className="btn btn-primary btn-xs" disabled={isLoading} onClick={refresh}>Coba Perbaharui Halaman</button>
          </td>
        </tr>
      )
    }

    if (isLoading) {
      return (
        <tr>
          <td colSpan={9} style={{textAlign: 'center'}}>
            Sedang memuat data &nbsp; <i className="fa fa-refresh fa-spin"></i>
          </td>
        </tr>
      );
    }

    if (listDokumenKerjasama.length === 0) {
      return (
        <tr>
          <td colSpan={9} style={{textAlign: 'center'}}>
            Data tidak ditemukan: &nbsp; <button className="btn btn-primary btn-xs" disabled={isLoading} onClick={refresh}>Coba Perbaharui Halaman</button>
          </td>
        </tr>
      );
    }
    
    return listDokumenKerjasama.map((dokumenKerjasama, index) => {
      return (
        <React.Fragment>
          <tr key={dokumenKerjasama.DOK_ID}>
            <td>{index + 1}</td>
            <td>{dokumenKerjasama.DOK_NOMOR}</td>
            <td>{moment(dokumenKerjasama.PERIODE_AWAL).format('DD MMMM YYYY')}</td>
            <td>{moment(dokumenKerjasama.PERIODE_AKHIR).format('DD MMMM YYYY')}</td>
            <td>Universitas Multimedia Nusantara</td>
            <td>Indonesia</td>
            <td>{dokumenKerjasama.CO_DETAIL[0]?.CO_NAME || '—'}</td>
            <td>{dokumenKerjasama.CO_DETAIL[0]?.CO_TYP_DETAIL.CO_TYP_NAME || '—'}</td>
            <td>{dokumenKerjasama.DOK_JENIS_DETAIL?.DOK_JENIS_NAMA}</td>
            <td>{dokumenKerjasama.STATUS_DETAIL?.STATUS_NAME}</td>
            <td>{renderBentukKerjasama(dokumenKerjasama.BENTUK_KERJASAMA)}</td>
          </tr>
          {renderCompanies(dokumenKerjasama.CO_DETAIL)}
        </React.Fragment>
      );
    })
  }

  return (
    <DokumenKerjasamaExportExceclWrapper>
      <section className="content">
        <div className="row">
          <div className="col-lg-12">
            <div className="box">
              <div className="box-header">
                <h3 className="box-title">Ekspor Dokumen Kerjasama</h3>
              </div>
              <div className="box-body">
                <div className="row">
                  <div className="col-md-4">
                    <p><b>Anda akan mengekspor data yang Anda pilih ke dalam format Microsoft Excel.</b> Berikut adalah tampilan data yang akan di ekspor ke Microsoft Excel. Apabila sudah yakin, silahkan tekan tombol `Ekspor ke Excel`</p>
                    <button
                      className="btn btn-primary"
                      style={{marginRight: '4px'}}
                      onClick={() => history.push('/dokumen-kerjasama')}
                    >
                      kembali
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={exportExcel}
                      disabled={isLoading}
                    >
                      Ekspor ke Excel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-12">
            <div className="box">
              <div className="box-header">
                <h3 className="box-title">Hasil Data Ekspor Excel</h3>
              </div>
              <div className="box-body table-responsive no-padding">
                <table className="table table-hover" id="main-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>No. Dokumen</th>
                      <th>Tanggal Mulai Kerjasama</th>
                      <th>Tanggal Selesai Kerjasama</th>
                      <th>Institusi</th>
                      <th>Negara Partner</th>
                      <th>Partner</th>
                      <th>Jenis Lembaga</th>
                      <th>Jenis Dokumen</th>
                      <th>Status</th>
                      <th>Bentuk Kerjasama</th>
                    </tr>
                  </thead>
                  <tbody>
                    { renderDokumenKerjasama() }
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </DokumenKerjasamaExportExceclWrapper>
  );
};

const DokumenKerjasamaExportExceclWrapper = styled.div``;
